import { ChangeEvent, KeyboardEvent, forwardRef, useId } from 'react';
import { isMobile as isMobileDevice } from 'react-device-detect';
import classNames from 'classnames';
import TextareaAutosize, {
  TextareaAutosizeProps,
} from 'react-textarea-autosize';
import styles from './TextareaSimple.module.scss';
import { KeyCodes } from 'src/types';
import { PlacesType } from 'react-tooltip';
import { HOVER_TOOLTIP_ID } from 'src/constants';
import { HoverTooltip } from '../HoverTooltip';

interface TextareaSimpleProps
  extends Omit<TextareaAutosizeProps, 'onChange' | 'onSubmit'> {
  value: string;
  isDisabled?: boolean;
  className?: string;
  onChange: (value: string) => void;
  onSubmit?: () => Promise<void> | void;
  tooltipContent?: string;
  tooltipId?: string;
  tooltipPlace?: PlacesType;
}

export const TextareaSimple = forwardRef<
  HTMLTextAreaElement,
  TextareaSimpleProps
>(
  (
    {
      value,
      isDisabled,
      className,
      onChange,
      onSubmit,
      tooltipContent,
      tooltipId,
      tooltipPlace,
      ...rest
    },
    ref,
  ) => {
    const id = useId();
    const preparedTooltipId = tooltipId || `${HOVER_TOOLTIP_ID}-${id}`;

    const handleChange = ({ target }: ChangeEvent<HTMLTextAreaElement>) => {
      if (!isDisabled) {
        onChange(target.value);
      }
    };

    const handleSubmit = (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (!onSubmit) {
        return;
      }

      const { key, shiftKey } = e;

      if (
        key === KeyCodes.ENTER &&
        !shiftKey &&
        !isMobileDevice &&
        !isDisabled
      ) {
        e.preventDefault();
        onSubmit();
      }
    };

    return (
      <>
        {tooltipContent && <HoverTooltip id={preparedTooltipId} />}

        <TextareaAutosize
          ref={ref}
          value={value}
          disabled={isDisabled}
          className={classNames(styles.root, className)}
          onChange={handleChange}
          onKeyDown={handleSubmit}
          data-tooltip-id={tooltipContent ? preparedTooltipId : undefined}
          data-tooltip-content={tooltipContent}
          data-tooltip-place={tooltipPlace ? tooltipPlace : undefined}
          {...rest}
        />
      </>
    );
  },
);
